/**
 * HowToWalkthrough — 4-step animated walkthrough shown to free-tier members
 * inside the /vault dashboard. Uses the E-book Writing track as the running
 * example so a newcomer can literally watch the "Pick → Copy → Paste → Ship"
 * loop happen. Auto-advances every 5s; hover pauses; clicking a step jumps.
 *
 * No real video — this is intentionally a lightweight animated walkthrough
 * per the user's Feb 2026 request. When they have a Loom, swap the outro
 * card's `<Link>` for an <iframe>.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Copy, Sparkles, PackageCheck, ArrowRight, Pause, Play } from "lucide-react";
import { VAULT } from "../data/vaultStats";

const STEPS = [
  {
    n: 1,
    verb: "Pick",
    sub: "a track",
    icon: BookOpen,
    accent: "purple",
    title: "Open E-book Writing",
    body: "You're picking Track 01. Twelve prompts, sequenced from niche-finder to launch-day marketing.",
    visual: {
      kind: "card",
      lines: [
        { label: "TRACK 01", value: "E-book Writing" },
        { label: "PROMPTS", value: "12 · sequenced for you" },
        { label: "OUTPUT", value: "Amazon KDP-ready book" },
      ],
    },
  },
  {
    n: 2,
    verb: "Copy",
    sub: "a prompt",
    icon: Copy,
    accent: "purple",
    title: "Copy prompt 01 · E-book Niche Finder",
    body: "One click copies the whole prompt to your clipboard — placeholders included.",
    visual: {
      kind: "prompt",
      title: "01 · E-book Niche Finder",
      snippet:
        "Act as a bestselling non-fiction publishing scout. Give me 10 profitable but under-saturated e-book niches for 2026. For each: audience size, buyer intent, competition density, and my ONE-line positioning wedge...",
    },
  },
  {
    n: 3,
    verb: "Paste",
    sub: "into ChatGPT",
    icon: Sparkles,
    accent: "purple",
    title: "Paste into ChatGPT (or Claude, or Gemini)",
    body: "Paid members run it in-app on GPT-5.2 — unlimited. Free tier runs it in their own AI account.",
    visual: {
      kind: "chat",
      user: "[pasted the E-book Niche Finder prompt]",
      ai: [
        "1. Menopause-Friendly Strength Training — high intent, low competition",
        "2. First-Time Landlords in the UK — buyer desperation × underserved",
        "3. Notion for Solo Therapists — small niche, cash-rich",
      ],
    },
  },
  {
    n: 4,
    verb: "Ship",
    sub: "the asset",
    icon: PackageCheck,
    accent: "amber",
    title: "12 prompts later — a live e-book on KDP",
    body: "Each track ends with a real asset in the world. Not another note in your Notion.",
    visual: {
      kind: "outcome",
      cover: "Menopause Strength · A 12-Week Plan",
      price: "£9.99 · Kindle & Paperback",
      status: "LIVE ON AMAZON KDP",
    },
  },
];

const AUTO_MS = 5200;

export function HowToWalkthrough() {
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (paused) return undefined;
    timerRef.current = setTimeout(() => {
      setStep((s) => (s + 1) % STEPS.length);
    }, AUTO_MS);
    return () => clearTimeout(timerRef.current);
  }, [step, paused]);

  const active = STEPS[step];
  const ActiveIcon = active.icon;

  return (
    <section
      className="relative border border-purple-500/25 bg-gradient-to-br from-[#0b0616] via-[#0a0a0a] to-[#100612] p-6 md:p-10 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="how-to-walkthrough"
    >
      {/* Ambient glow */}
      <div
        className="absolute inset-0 opacity-[0.15] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 20%, rgba(168,85,247,0.4), transparent 55%), radial-gradient(circle at 85% 80%, rgba(176,112,255,0.15), transparent 55%)",
        }}
      />

      <div className="relative">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8 md:mb-10">
          <div>
            <div className="text-purple-400 text-xs md:text-sm tracking-[0.35em] uppercase mb-3 font-mono flex items-center gap-2">
              <Sparkles size={12} strokeWidth={2.5} className="text-purple-300" />
              How the vault actually works
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05] text-white max-w-2xl">
              Pick → Copy → Paste →{" "}
              <span className="text-amber-300 drop-shadow-[0_0_20px_rgba(176,112,255,0.35)]">
                Ship
              </span>
            </h2>
            <p className="mt-4 text-zinc-400 text-base md:text-lg max-w-xl">
              Watch a free user turn Track 01 into a live e-book in four moves.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            className="inline-flex items-center gap-2 border border-purple-500/40 hover:border-purple-500/80 text-purple-300 hover:text-purple-200 px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase transition-colors"
            data-testid="walkthrough-pause"
            aria-label={paused ? "Play walkthrough" : "Pause walkthrough"}
          >
            {paused ? <Play size={12} /> : <Pause size={12} />}
            {paused ? "Play" : "Pause"}
          </button>
        </div>

        {/* Step tabs */}
        <div className="grid grid-cols-4 gap-1 md:gap-2 mb-6 md:mb-8" data-testid="walkthrough-steps">
          {STEPS.map((s, i) => {
            const isActive = i === step;
            const isDone = i < step;
            return (
              <button
                key={s.n}
                type="button"
                onClick={() => setStep(i)}
                className={`text-left px-2 md:px-4 py-3 border-t-2 transition-all duration-300 ${
                  isActive
                    ? "border-purple-400 bg-purple-500/10"
                    : isDone
                    ? "border-purple-500/40 bg-purple-500/[0.03]"
                    : "border-zinc-800 bg-transparent hover:border-zinc-700"
                }`}
                data-testid={`walkthrough-step-${s.n}`}
                aria-pressed={isActive}
              >
                <div className={`text-[9px] md:text-[10px] tracking-[0.3em] font-mono ${isActive ? "text-purple-300" : "text-zinc-500"}`}>
                  0{s.n}
                </div>
                <div className={`font-display text-sm md:text-lg font-bold tracking-tight mt-0.5 ${isActive ? "text-white" : isDone ? "text-zinc-300" : "text-zinc-500"}`}>
                  {s.verb}
                </div>
                <div className="hidden md:block text-[10px] text-zinc-500 mt-0.5 tracking-wide">{s.sub}</div>
              </button>
            );
          })}
        </div>

        {/* Active step body */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-start">
          {/* Left · copy */}
          <div key={`copy-${step}`} className="animate-[fadeSlide_.5s_ease_forwards]">
            <div className="inline-flex items-center gap-2 text-purple-300 text-[10px] tracking-[0.3em] uppercase mb-4">
              <ActiveIcon size={12} strokeWidth={2.5} />
              Step 0{active.n} · {active.verb}
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white leading-tight mb-4">
              {active.title}
            </h3>
            <p className="text-zinc-300 text-base md:text-lg leading-relaxed">
              {active.body}
            </p>
          </div>

          {/* Right · dynamic visual */}
          <div key={`visual-${step}`} className="animate-[fadeSlide_.5s_ease_forwards]">
            <WalkthroughVisual visual={active.visual} />
          </div>
        </div>

        {/* CTA row */}
        <div className="mt-8 md:mt-10 pt-6 border-t border-purple-500/15 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-zinc-500 text-sm">
            First 2 tracks + 10 AI runs a day are free. Unlock all {VAULT.tracks} tracks for £25 one-time.
          </p>
          <div className="flex items-center gap-3">
            <Link
              to="/vault/ebook-writing"
              className="inline-flex items-center gap-2 border border-purple-500/60 hover:bg-purple-500 hover:text-black text-purple-200 px-5 py-2.5 text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300"
              data-testid="walkthrough-cta-track"
            >
              Try Track 01 now <ArrowRight size={14} />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black px-5 py-2.5 text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300 shadow-[0_0_20px_rgba(176,112,255,0.3)]"
              data-testid="walkthrough-cta-upgrade"
            >
              Unlock all 83 <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlide {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}

function WalkthroughVisual({ visual }) {
  if (visual.kind === "card") {
    return (
      <div className="bg-[#050308] border border-purple-500/40 p-6 md:p-7 shadow-[0_0_30px_-10px_rgba(168,85,247,0.5)]">
        <div className="text-purple-300 text-[10px] tracking-[0.3em] uppercase mb-4 font-mono">
          · Now open ·
        </div>
        <div className="space-y-3">
          {visual.lines.map((l) => (
            <div key={l.label} className="flex items-baseline justify-between gap-4 py-2 border-b border-zinc-900 last:border-b-0">
              <span className="text-zinc-500 text-[10px] tracking-[0.25em] uppercase font-mono">{l.label}</span>
              <span className="text-white text-sm md:text-base font-medium text-right">{l.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (visual.kind === "prompt") {
    return (
      <div className="bg-[#050308] border border-purple-500/40 shadow-[0_0_30px_-10px_rgba(168,85,247,0.5)]">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-purple-500/20 bg-purple-500/[0.05]">
          <span className="text-purple-300 text-[10px] tracking-[0.3em] uppercase font-mono">{visual.title}</span>
          <span className="inline-flex items-center gap-1.5 text-emerald-300 text-[10px] tracking-[0.25em] uppercase font-mono animate-pulse">
            <Copy size={11} strokeWidth={2.5} />
            Copied
          </span>
        </div>
        <p className="p-4 md:p-5 text-zinc-300 text-sm leading-relaxed font-mono">{visual.snippet}</p>
      </div>
    );
  }
  if (visual.kind === "chat") {
    return (
      <div className="bg-[#050308] border border-purple-500/40 shadow-[0_0_30px_-10px_rgba(168,85,247,0.5)]">
        <div className="px-4 py-2.5 border-b border-purple-500/20 bg-purple-500/[0.05]">
          <span className="text-purple-300 text-[10px] tracking-[0.3em] uppercase font-mono">ChatGPT · GPT-5.2</span>
        </div>
        <div className="p-4 md:p-5 space-y-3">
          <div className="text-zinc-500 text-xs font-mono italic border-l-2 border-purple-500/60 pl-3 py-1">
            {visual.user}
          </div>
          <div className="space-y-2 pt-2">
            {visual.ai.map((line, i) => (
              <div
                key={i}
                className="text-zinc-200 text-sm leading-relaxed opacity-0 animate-[typeIn_.4s_ease_forwards]"
                style={{ animationDelay: `${i * 400}ms` }}
              >
                {line}
              </div>
            ))}
          </div>
        </div>
        <style>{`
          @keyframes typeIn {
            from { opacity: 0; transform: translateX(-6px); }
            to { opacity: 1; transform: translateX(0); }
          }
        `}</style>
      </div>
    );
  }
  if (visual.kind === "outcome") {
    return (
      <div className="bg-gradient-to-br from-[#0a0704] via-[#050308] to-[#0a0308] border border-amber-400/60 p-6 md:p-8 text-center shadow-[0_0_40px_-10px_rgba(176,112,255,0.5)]">
        <div className="text-amber-300 text-[10px] tracking-[0.35em] uppercase mb-4 font-mono">
          · Live asset ·
        </div>
        <div className="mx-auto w-24 h-32 md:w-28 md:h-40 bg-gradient-to-br from-purple-600 via-purple-800 to-black border border-amber-400/40 flex items-end p-3 mb-4 shadow-[0_0_25px_rgba(168,85,247,0.6)]">
          <span className="font-display text-white text-[10px] md:text-xs font-bold leading-tight text-left">
            {visual.cover}
          </span>
        </div>
        <div className="text-white text-base md:text-lg font-medium mb-2">{visual.cover}</div>
        <div className="text-zinc-400 text-sm mb-4">{visual.price}</div>
        <span className="inline-flex items-center gap-1.5 border border-emerald-400/60 text-emerald-300 text-[10px] tracking-[0.3em] uppercase px-3 py-1 font-bold animate-pulse">
          <PackageCheck size={12} strokeWidth={2.5} />
          {visual.status}
        </span>
      </div>
    );
  }
  return null;
}
